import React, { useState, useEffect } from 'react';
import { Clock, ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * Processing step status
 */
const STEP_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

/**
 * ProcessingUploader - File processing progress component
 * 
 * @param {Object} props
 * @param {File} props.file - Uploaded file
 * @param {number} props.pageCount - File page count
 * @param {string} props.uploadTime - Upload time description
 * @param {Function} props.onComplete - Processing complete callback
 * @param {Function} props.onRetry - Retry callback
 * @param {Function} props.onBack - Back callback
 * @param {Function} props.onContinue - Continue callback
 * @param {string} props.backButtonText - Back button text
 * @param {string} props.continueButtonText - Continue button text
 * @param {boolean} props.showActions - Whether to show bottom buttons
 * @param {Array} props.steps - Processing steps configuration
 */
const ProcessingUploader = ({
  file,
  pageCount = 24,
  uploadTime = '2 min ago',
  onComplete,
  onRetry,
  onBack,
  onContinue,
  backButtonText = 'Back',
  continueButtonText = 'Continue',
  showActions = true,
  steps: customSteps,
}) => {
  // Default processing steps
  const defaultSteps = [
    { id: 'upload', label: 'File Upload', status: STEP_STATUS.COMPLETED },
    { id: 'ocr', label: 'OCR Text Extraction', status: STEP_STATUS.IN_PROGRESS, progress: 0, estimatedTime: '~30s' },
    { id: 'analysis', label: 'AI-Powered Analysis', status: STEP_STATUS.PENDING },
  ];

  const [steps, setSteps] = useState(customSteps || defaultSteps);
  const [currentStepIndex, setCurrentStepIndex] = useState(1);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(1) + ' MB';
  };

  // Simulate processing progress
  useEffect(() => {
    const currentStep = steps[currentStepIndex];
    if (!currentStep || currentStep.status !== STEP_STATUS.IN_PROGRESS) return;

    const interval = setInterval(() => {
      setSteps(prevSteps => {
        const newSteps = [...prevSteps];
        const step = newSteps[currentStepIndex];
        
        if (step.progress < 100) {
          step.progress = Math.min(step.progress + Math.random() * 15, 100);
          
          if (step.progress >= 100) {
            step.status = STEP_STATUS.COMPLETED;
            
            // Move to next step
            if (currentStepIndex < newSteps.length - 1) {
              setCurrentStepIndex(prev => prev + 1);
              newSteps[currentStepIndex + 1].status = STEP_STATUS.IN_PROGRESS;
              newSteps[currentStepIndex + 1].progress = 0;
            } else {
              // All steps completed
              onComplete?.();
            }
          }
        }
        
        return newSteps;
      });
    }, 500);

    return () => clearInterval(interval);
  }, [currentStepIndex, steps, onComplete]);

  // Handle retry
  const handleRetry = (stepId) => {
    setSteps(prevSteps => {
      const newSteps = [...prevSteps];
      const stepIndex = newSteps.findIndex(s => s.id === stepId);
      if (stepIndex !== -1) {
        newSteps[stepIndex].status = STEP_STATUS.IN_PROGRESS;
        newSteps[stepIndex].progress = 0;
        newSteps[stepIndex].error = null;
        setCurrentStepIndex(stepIndex);
      }
      return newSteps;
    });
    onRetry?.(stepId);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case STEP_STATUS.COMPLETED:
        return 'var(--color-success)';
      case STEP_STATUS.IN_PROGRESS:
        return 'var(--color-primary)';
      case STEP_STATUS.FAILED:
        return 'var(--color-danger)';
      default:
        return 'var(--color-text-muted)';
    }
  };

  // Get status text
  const getStatusText = (step) => {
    switch (step.status) {
      case STEP_STATUS.COMPLETED:
        return 'Completed';
      case STEP_STATUS.IN_PROGRESS:
        return 'In Progress...';
      case STEP_STATUS.FAILED:
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  // Get status text color
  const getStatusTextColor = (status) => {
    switch (status) {
      case STEP_STATUS.COMPLETED:
        return 'var(--color-success)';
      case STEP_STATUS.IN_PROGRESS:
        return 'var(--color-primary)';
      case STEP_STATUS.FAILED:
        return 'var(--color-danger)';
      default:
        return 'var(--color-text-muted)';
    }
  };

  return (
    <>
      <div 
        className="rounded-2xl p-6"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        {/* File info header */}
        <div className="flex items-center gap-4 mb-6">
          {/* PDF icon */}
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-primary-light)' }}
          >
            <span 
              className="text-xs font-bold"
              style={{ color: 'var(--color-primary)' }}
            >
              PDF
            </span>
          </div>
          
          {/* File details */}
          <div>
            <h3 
              className="font-semibold text-lg"
              style={{ color: 'var(--color-text)' }}
            >
              {file?.name || 'Document.pdf'}
            </h3>
            <p 
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {pageCount} pages • {formatFileSize(file?.size)} • Uploaded {uploadTime}
            </p>
          </div>
        </div>

        {/* Processing steps */}
        <div className="relative pl-4">
          {/* Left vertical line */}
          <div 
            className="absolute left-0 top-0 bottom-0 w-1 rounded-full"
            style={{ backgroundColor: 'var(--color-primary)' }}
          />

          <div className="space-y-6">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-start gap-3">
                {/* Status icon */}
                <div 
                  className="w-5 h-5 rounded flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: getStatusColor(step.status) }}
                />
                
                {/* Step content */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Step name */}
                    <span 
                      className="font-medium"
                      style={{ color: 'var(--color-text)' }}
                    >
                      {step.label}
                    </span>
                    
                    {/* Status text */}
                    <span 
                      className="text-sm font-medium"
                      style={{ color: getStatusTextColor(step.status) }}
                    >
                      {getStatusText(step)}
                    </span>

                    {/* Show error info and retry button on failure */}
                    {step.status === STEP_STATUS.FAILED && (
                      <>
                        <span 
                          className="text-sm"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {step.error || 'Unable to process'}
                        </span>
                        <button
                          onClick={() => handleRetry(step.id)}
                          className="px-4 py-1.5 text-sm font-medium rounded-lg transition-all hover:shadow-md"
                          style={{ 
                            backgroundColor: 'var(--color-primary)',
                            color: 'var(--color-button-primary-text)'
                          }}
                        >
                          Retry
                        </button>
                      </>
                    )}
                  </div>

                  {/* Progress bar - only show when in progress */}
                  {step.status === STEP_STATUS.IN_PROGRESS && (
                    <div className="flex items-center gap-3 mt-2">
                      {/* Progress bar */}
                      <div className="flex-1 max-w-xs">
                        <div 
                          className="h-1.5 rounded-full overflow-hidden"
                          style={{ backgroundColor: 'var(--color-border)' }}
                        >
                          <div 
                            className="h-full rounded-full transition-all duration-300"
                            style={{ 
                              width: `${step.progress || 0}%`,
                              backgroundColor: 'var(--color-primary)'
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Percentage */}
                      <span 
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        {Math.round(step.progress || 0)}%
                      </span>

                      {/* Loading animation dots */}
                      <div className="flex gap-1">
                        <span 
                          className="w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ backgroundColor: 'var(--color-primary)' }}
                        />
                        <span 
                          className="w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ backgroundColor: 'var(--color-primary)', animationDelay: '0.2s' }}
                        />
                        <span 
                          className="w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ backgroundColor: 'var(--color-primary)', animationDelay: '0.4s' }}
                        />
                      </div>

                      {/* Estimated time */}
                      {step.estimatedTime && (
                        <div 
                          className="flex items-center gap-1 px-3 py-1 rounded-full text-xs"
                          style={{ 
                            backgroundColor: 'var(--color-background)',
                            color: 'var(--color-text-secondary)'
                          }}
                        >
                          <Clock className="w-3 h-3" />
                          <span>{step.estimatedTime}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom action buttons - outside card */}
      {showActions && (
        <div className="flex justify-between items-center mt-6">
          {/* Back button */}
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-8 py-3 font-medium rounded-xl transition-all duration-200 hover:opacity-80"
            style={{ 
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)'
            }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{backButtonText}</span>
          </button>
          
          {/* Continue button */}
          <button
            onClick={onContinue}
            disabled={steps.some(s => s.status === STEP_STATUS.IN_PROGRESS || s.status === STEP_STATUS.FAILED)}
            className={`inline-flex items-center gap-2 px-8 py-3 font-medium rounded-xl transition-all duration-200 ${
              steps.every(s => s.status === STEP_STATUS.COMPLETED) ? 'hover:shadow-md' : 'opacity-50 cursor-not-allowed'
            }`}
            style={{ 
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-button-primary-text)'
            }}
          >
            <span>{continueButtonText}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
};

export default ProcessingUploader;
